import type React from 'react';
import { useRef, useState } from 'react';
import type { CurveSettings } from '@/components/WellLoggingOperations/types/curveSettings';
import { LINES } from '@/components/WellLoggingOperations/TabletSettings/CurveSettings/CurveSettingsModal';
import type {
    ResearchesByDate,
    ResearchesByType,
    Research,
    CurveDisplaySettings,
} from '@/components/WellLoggingOperations/types';
import type { Gis, Scale, LocalCurves, LocalResearches } from './types';
import { CODE_COPY_STRING, CURVE_COPY_NUMBER, DEFAULT_STEP, KEY_COPY_STRING } from './constants';
import { cloneDeep, isNumber, isEqual } from 'lodash';

const MAX_CAROTAGE_COLUMN_SYMBOLS_LENGTH = 18;
export const mappedOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(item => ({ value: item, label: item }));

export const findLineType = (item: CurveSettings): number => {
    const lineType = Object.values(LINES).find(line => {
        return line.lineType === item.lineType && line.lineDisplay === item.lineDisplay;
    });

    if (lineType) {
        return lineType.value;
    }

    return LINES.NormalLine.value;
};

export const filteredGis = (gis: Gis[][]) => {
    const res: Gis[][] = [[]];
    const minColumnIndex = gis && Math.min(...gis.map(g => g.map(c => c.curveData?.columnIndex ?? 0)).flat());
    // offset необходим в тех случаях, когда мы переносим из первой колонки влево, то есть columnIndex становится отрицательным
    // в массиве не может быть отрицательного индекса, поэтому мы это должно скомпенсировать
    // ко всем индексам добавляем offset, чтобы те, что меньше нуля оказались положительными
    const offset = minColumnIndex < 0 ? -minColumnIndex : 0;
    gis?.forEach(g =>
        g?.forEach(c => {
            const columnIndex = c.settings?.columnNum ? c.settings?.columnNum - 1 : 0;
            const safeColumnsIndex = columnIndex + offset;
            if (res[safeColumnsIndex]) {
                res[safeColumnsIndex].push(c);
            } else {
                res[safeColumnsIndex] = [];
                res[safeColumnsIndex].push(c);
            }
        }),
    );

    return res.filter(g => {
        return (
            g.length &&
            g.filter(c => {
                return !!c;
            })
        );
    });
};

export const calcColumnWidth = (data: Gis[][]) => {
    let columnsWidthArray: number[][] = [];
    if (data.length > 0) {
        columnsWidthArray = data.map(chart =>
            chart.map(line => {
                return (
                    (line.curveData.xStop?.toFixed(1).length ?? 0) +
                    (line.curveData.name?.length ?? 0) +
                    (line.curveData.units?.length ?? 0) +
                    (line.curveData.xStart?.toFixed(1).length ?? 0) +
                    3
                );
            }),
        );
    }
    return columnsWidthArray;
};

export const calcWidthToAdd = (lengthArr: number[][], fontSize: number, width: number) => {
    const flatArr = lengthArr.flat();
    let lengthDiff = 0;
    if (flatArr.length > 0) {
        for (let i = 0; i < flatArr.length; i++) {
            if (Math.round(fontSize * 0.6 * flatArr[i]) > width) {
                const diff = flatArr[i] - MAX_CAROTAGE_COLUMN_SYMBOLS_LENGTH;
                if (diff > lengthDiff) lengthDiff = Math.round(fontSize * 0.6 * diff);
            }
        }
    }
    return lengthDiff;
};

export const resolveLineType = (lineSettings: CurveSettings) => {
    if (lineSettings.dashed) {
        return 'dashed';
    }
    if (lineSettings.lineDisplay === 'dashed') {
        return 'dashed';
    }
    if (lineSettings.lineDisplay === 'dotted') {
        return 'dotted';
    }
    return 'solid';
};

export const findChildrenByKey = (data: any[], key: string): any[] => {
    for (const item of data) {
        if (item.key === key) {
            if (item.children) {
                return item.children;
            }
        }

        if (item.children) {
            const result = findChildrenByKey(item.children, key);
            if (result.length > 0) {
                return result;
            }
        }
    }

    return [];
};

export const findChildrenByParentCurve = (data: ResearchesByDate[], parentCurve: number): any[] => {
    const children = [];
    for (const item of data) {
        if (!item.children && item?.parentCurve && item?.parentCurve === parentCurve) {
            children.push(item);
        }

        if (item.children) {
            const nestedChildren = findChildrenByParentCurve(item.children, parentCurve);
            children.push(...nestedChildren);
        }
    }

    return children;
};

export const findParentKey = (
    data: ResearchesByDate[] | ResearchesByType[] | Research[],
    targetKey: string,
): string | null => {
    for (const item of data) {
        if (item.children) {
            if (item.children.some((child: { key: string }) => child.key === targetKey)) {
                return item.key;
            }

            const result = findParentKey(item.children, targetKey);
            if (result) {
                return result;
            }
        }
    }
    return null;
};

export const replaceChildrenByParentKey = (data: any[], parentKey: string, newChildren: any[]): any[] => {
    return data.map(item => {
        if (item.key === parentKey) {
            return { ...item, children: [...newChildren] };
        }

        if (item.children) {
            return { ...item, children: replaceChildrenByParentKey(item.children, parentKey, newChildren) };
        }

        return item;
    });
};

export const findChildByCurve = (data: ResearchesByDate[], curve: number): Research | null => {
    for (const item of data) {
        if (item.children) {
            const found = item.children.find(child => (child as Research).curve === curve);
            if (found) {
                return found as Research;
            }

            const childFound = findChildByCurve(item.children as ResearchesByDate[], curve);
            if (childFound) return childFound;
        }
    }
    return null;
};

export const removeChildByKey = (children: any[], childKey: string): any[] => {
    return children
        .map(child => ({
            ...child,
            children: child.children ? removeChildByKey(child.children, childKey) : undefined,
        }))
        .filter(child => child.key !== childKey);
};

export const removeChildByCurve = (children: any[], childCurve: number): any[] => {
    return children
        .map(child => ({
            ...child,
            children: child.children ? removeChildByCurve(child.children, childCurve) : undefined,
        }))
        .filter(child => child.curve !== childCurve);
};

export const getChildrenCurves = (parentCurve: number): number[] => {
    return [Number(`${parentCurve}00001`), Number(`${parentCurve}00002`)];
};

export const calculateXStep = (xStart: number, xStop: number, desiredSteps = 10): number => {
    const range = Math.abs(xStop - xStart);
    return range / desiredSteps;
};

export const getTicks = (
    min?: number,
    max?: number,
    xStepLine?: number,
    isDecimalLog = false,
): Array<{ value: number | null; percent: number }> => {
    const ticks: Array<{ value: number | null; percent: number }> = [];

    if (min === undefined || max === undefined || min >= max) return ticks;

    if (isDecimalLog) {
        const minExp = Math.log10(min);
        const maxExp = Math.log10(max);

        for (let exp = Math.floor(minExp); exp <= Math.ceil(maxExp); exp++) {
            for (let i = 1; i < 10; i++) {
                const value = i * Math.pow(10, exp);
                if (value < min || value > max) continue;

                const percent = ((Math.log10(value) - minExp) / (maxExp - minExp)) * 100;
                if (i !== 1 || exp !== Math.floor(minExp)) {
                    ticks.push({
                        value: i === 1 && exp !== Math.ceil(maxExp) ? value : null,
                        percent,
                    });
                }
            }
        }

        return ticks;
    } else {
        const xStep = xStepLine || calculateXStep(min, max);
        const range = max - min;
        const count = Math.floor(range / xStep);
        const fullTicks = [];

        for (let i = 1; i <= count; i++) {
            const value = min + i * xStep;
            const percent = ((value - min) / (max - min)) * 100;
            fullTicks.push({ value, percent });
        }

        const indexesToShow = processArray(fullTicks.map((_, i) => i));
        return fullTicks.map((tick, i) =>
            indexesToShow.includes(i + 1) ? tick : { value: null, percent: tick.percent },
        );
    }
};

export const processArray = (arr: number[]): number[] => {
    const result: number[] = [];
    if (arr.length >= 2) {
        if (arr.length < 8) {
            // при нечетном берем серединку, иначе два крайних
            if ((arr.length + 1) % 2 === 1) {
                const middleElement = arr.length / 2;
                result.push(middleElement);
            } else {
                const leftElement = Math.floor(arr.length / 3);
                const rightElement = Math.ceil((2 * arr.length) / 3);
                result.push(leftElement, rightElement);
            }
            result.sort();
        } else {
            const middleElement = Math.round(arr.length / 2);
            const leftElement = Math.floor(arr.length / 4);
            const rightElement = Math.ceil((3 * arr.length) / 4);
            result.push(leftElement, middleElement, rightElement);
        }
    }

    return result;
};

export const makeDefaultValue = (index: number, scales: Scale[]): Scale | null => {
    if (!scales.length || !scales[0]) {
        console.warn(`makeDefaultValue(${index}): scales[0] отсутствует`, scales);
        return null;
    }

    return {
        ...scales[0],
        key: `${scales[0].key}${KEY_COPY_STRING}${index}`,
        code: `${scales[0].code}${CODE_COPY_STRING}${index}`,
        curve: scales[0].curve * CURVE_COPY_NUMBER + index,
        title: `${scales[0].title} | ${index}`,
        parentCurve: scales[0].curve,
    };
};

export const removeScale = (
    items: Scale[],
    localResearches: LocalResearches,
    localCurves: LocalCurves,
    scales: Scale[],
    wellId: number,
    onChangeLocalCurves: (value: LocalCurves) => void,
    onChangeLocalResearches: (value: LocalResearches) => void,
) => {
    const researches = cloneDeep(localResearches);
    const updatedCurves = { ...localCurves };
    const initialScale = scales[0];
    items.forEach(scale => {
        if (scale) {
            const newChildren = removeChildByKey(researches[wellId], scale.key);
            if (initialScale) {
                researches[wellId] = newChildren as ResearchesByDate[];
            }

            delete updatedCurves[scale.curve.toString()];
        }
    });

    onChangeLocalCurves(updatedCurves);
    onChangeLocalResearches(researches);
};

export const addScale = (
    items: Scale[],
    localResearches: LocalResearches,
    localCurves: LocalCurves,
    scales: Scale[],
    wellId: number,
    onChangeLocalCurves: (value: LocalCurves) => void,
    onChangeLocalResearches: (value: LocalResearches) => void,
    savedScale: boolean,
) => {
    const initialScale = scales[0];
    const lastScale = [...scales].reverse().find(s => s);

    if (lastScale?.curve && initialScale?.curve) {
        const lastCurve = localCurves[lastScale.curve];
        const researches = cloneDeep(localResearches);
        const curves = cloneDeep(localCurves);
        items.forEach((scale: Scale) => {
            const parentKey = findParentKey(researches[wellId], initialScale.key);

            if (parentKey && scale) {
                const children: Research[] = findChildrenByKey(researches[wellId], parentKey) as Research[];

                if (!children.find((child): child is Research => child.key === scale.key)) {
                    const indexOfParent = children.findLastIndex(oc => oc.key === scale.key);
                    children.splice(children.length - indexOfParent, 0, scale);

                    researches[wellId] = replaceChildrenByParentKey(
                        researches[wellId],
                        parentKey,
                        children,
                    ) as ResearchesByDate[];

                    if (savedScale) {
                        curves[scale.curve] = {
                            ...lastCurve,
                            id: scale.curve,
                            xStart: localCurves[scale.curve].xStart,
                            xStop: localCurves[scale.curve].xStop,
                            xStep: localCurves[scale.curve].xStep,
                            parentCurve: scale?.parentCurve ?? undefined,
                            key: scale.key,
                        };
                    } else
                        curves[scale.curve] = {
                            ...lastCurve,
                            id: scale.curve,
                            xStart: localCurves[initialScale.curve].xStart,
                            xStop: localCurves[initialScale.curve].xStop,
                            parentCurve: scale?.parentCurve ?? undefined,
                            key: scale.key,
                        };
                }
            }
        });

        onChangeLocalCurves(curves);
        onChangeLocalResearches(researches);
    } else {
        throw new Error('Непредвиденная ошибка');
    }
};

export const onNumberOfScalesChange = (
    value: number | undefined | string,
    scales: Scale[],
    setScales: React.Dispatch<React.SetStateAction<Scale[]>>,
    localResearches: LocalResearches,
    localCurves: LocalCurves,
    wellId: number,
    onChangeLocalCurves: (value: LocalCurves) => void,
    onChangeLocalResearches: (value: LocalResearches) => void,
    skipAutoAddChildrenScalesRef: React.MutableRefObject<boolean>,
    curveDisplaySettings: CurveDisplaySettings,
) => {
    if (!isNumber(value)) return;

    skipAutoAddChildrenScalesRef.current = true;
    while (scales.length < 3) {
        scales.push(null);
    }
    if (value > scales.filter(s => s?.key).length) {
        let scalesToAdd: Scale[] = [];

        if (value === 1 && curveDisplaySettings.isScales && curveDisplaySettings.childrenCurves?.length) {
            const parentScale = scales[0];
            if (parentScale)
                scalesToAdd = curveDisplaySettings.childrenCurves.map(child => {
                    localCurves[child.curve] = {
                        ...localCurves[parentScale.curve],
                        id: child.curve,
                        parentCurve: parentScale.curve,
                        xStart: child.xStart,
                        xStep: child.xStep ?? DEFAULT_STEP,
                        xStop: child.xStop,
                    };
                    return {
                        ...parentScale,
                        id: child.curve,
                        curve: child.curve,
                        key: child.key,
                        parentCurve: parentScale.curve,
                        title: `${parentScale.title} | ${child.key[child.key.length - 1]}`,
                        code: `${parentScale.code}000000${child.key[child.key.length - 1]}`,
                    };
                });
        } else {
            scales.forEach((s, i) => {
                if (i > 0 && i < value) {
                    const newScale = makeDefaultValue(i, scales);
                    if (newScale && !scales.find(scale => scale?.key === newScale.key)) {
                        scalesToAdd.push(newScale);
                    }
                }
            });
        }

        if (scalesToAdd.length > 0) {
            setScales(prevScales => {
                const newScales = [...prevScales];
                let insertIndex = newScales.length;

                for (let j = newScales.length - 1; j >= 0; j--) {
                    if (newScales[j] !== null) {
                        insertIndex = j + 1;
                        break;
                    }
                }

                newScales.splice(insertIndex, 0, ...scalesToAdd);
                const sliced = newScales.slice(0, 3);

                while (sliced.length < 3) {
                    sliced.push(null);
                }

                return sliced;
            });

            addScale(
                scalesToAdd,
                localResearches,
                localCurves,
                scales,
                wellId,
                onChangeLocalCurves,
                onChangeLocalResearches,
                false,
            );
        }
    } else {
        const scalesToRemove: Scale[] = [];

        scales.forEach((s, i) => {
            if (i !== 0 && i > value - 1) {
                if (s) {
                    scalesToRemove.push(s);
                }
            }
        });

        if (scalesToRemove.length > 0) {
            setScales(prevScales => {
                const updatedScales = prevScales.filter(item => !scalesToRemove.find(rm => rm.key === item?.key));
                const newScales = updatedScales.slice(0, 3);
                const filledScales = [...newScales, ...new Array(3 - newScales.length).fill(null)];
                return filledScales as Scale[];
            });

            removeScale(
                scalesToRemove,
                localResearches,
                localCurves,
                scales,
                wellId,
                onChangeLocalCurves,
                onChangeLocalResearches,
            );
        }
    }
};

export const ensureAllKeysInArray = (obj: Record<string, any>, arr: string[]): string[] => {
    const objKeys = Object.keys(obj);
    const missingKeys = objKeys.filter(key => !arr.includes(key));
    return [...arr, ...missingKeys];
};

export const findChildernByParentCurve = (data: LocalCurves, parentCurve: number) => {
    return Object.entries(data)
        .filter(([key, value]) => value.parentCurve === parentCurve)
        .map(([_, value]) => value);
};

export const removeChildrenCurvesByParentCurve = (localCurves: LocalCurves, parentCurve: number) => {
    for (const key in localCurves) {
        if (localCurves[key]?.parentCurve === parentCurve) {
            delete localCurves[key];
        }
    }
    return localCurves;
};

const uniqueByCurve = (arr: Gis[]) => {
    const seen = new Set();
    return arr.filter(item => {
        if (item) {
            if (seen.has(item.curve)) return false;
            seen.add(item.curve);
            return true;
        }
    });
};

export const insertChildrenCurves = (data: Gis[][][]): Gis[][][] => {
    data.forEach(groups => {
        groups.forEach((group, groupIndex) => {
            let updatedGroup: Gis[] = [];

            group.forEach(item => {
                updatedGroup.push(item);
                const children = item.displaySettings?.childrenCurves;
                if (!item.parentCurve && Array.isArray(children) && children.length > 0) {
                    const curveData = item.curveData;
                    const displaySettings = item.displaySettings;

                    const childrenItems = children
                        .map((child, i) => {
                            if (!child) return null;

                            const baseItem = structuredClone(item);

                            return {
                                ...baseItem,
                                curve: child.curve,
                                key: child.key,
                                id: child.curve,
                                parentCurve: child.parentCurve,
                                title: `${baseItem.title} | ${i + 1}`,
                                code: `${baseItem.code}000000${i + 1}`,
                                displaySettings: {
                                    ...displaySettings,
                                    childrenCurves: null,
                                    scaleProps: {
                                        xStart: child.xStart,
                                        xStep: child.xStep,
                                        xStop: child.xStop,
                                    },
                                },
                                curveData: {
                                    ...curveData,
                                    id: child.curve,
                                    key: child.key,
                                    parentCurve: baseItem.curve,
                                    xStart: child.xStart,
                                    xStep: child.xStep,
                                    xStop: child.xStop,
                                },
                            } as Gis;
                        })
                        .filter((item): item is Gis => item !== null);
                    updatedGroup.push(...childrenItems);
                }
                updatedGroup = uniqueByCurve(updatedGroup);
            });

            groups[groupIndex] = updatedGroup;
        });
    });

    return data;
};

export function useDeepCompareMemo<T>(factory: () => T, dependencies: any[]): T {
    const ref = useRef<any[]>([]);
    const [value, setValue] = useState<T>(() => factory());

    if (!isEqual(dependencies, ref.current)) {
        ref.current = dependencies;
        setValue(factory());
    }

    return value;
}
